import { DatePipe, UpperCasePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { RouterLink } from "@angular/router";
import { DspAdvertiserRequest, DspAdvertiserRequestStatus } from "@front/m19-api-client";
import { DspAdvertiserRequestService } from "@front/m19-services";
import { IAlertComponent, IBadgeComponent, IButtonDirective, IIconComponent, ITooltipDirective } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { interval, timer } from "rxjs";

const AUTO_RELOAD_INTERVAL_MS = 15_000;
const MANUAL_RELOAD_COOLDOWN_MS = 5_000;

@Component({
  selector: "app-request-creation-status-page",
  imports: [
    IBadgeComponent,
    IButtonDirective,
    DatePipe,
    TranslocoDirective,
    IAlertComponent,
    UpperCasePipe,
    RouterLink,
    IIconComponent,
    ITooltipDirective,
  ],
  templateUrl: "./request-creation-status-page.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestCreationStatusPageComponent {
  private readonly dspAdvertiserRequestService = inject(DspAdvertiserRequestService);
  private readonly destroyRef = inject(DestroyRef);

  readonly DspAdvertiserRequestStatus = DspAdvertiserRequestStatus;

  readonly dspAdvertiserRequest = input.required<DspAdvertiserRequest>();

  protected readonly isReloadingStatus = signal(false);

  protected readonly isPartiallyCompleted = computed(
    () =>
      this.dspAdvertiserRequest().status === DspAdvertiserRequestStatus.PARTIALLY_COMPLETED ||
      this.dspAdvertiserRequest().status === DspAdvertiserRequestStatus.PARTIALLY_COMPLETED_NOT_LINKED,
  );

  protected readonly isCompleted = computed(
    () => this.dspAdvertiserRequest().status === DspAdvertiserRequestStatus.COMPLETED || this.isPartiallyCompleted(),
  );

  protected readonly isRejected = computed(
    () => this.dspAdvertiserRequest().status === DspAdvertiserRequestStatus.REJECTED,
  );

  protected readonly needsFormCorrection = computed(
    () =>
      this.dspAdvertiserRequest().status === DspAdvertiserRequestStatus.PARTIALLY_COMPLETE_BILLING_UNSET ||
      this.dspAdvertiserRequest().status === DspAdvertiserRequestStatus.PARTIALLY_COMPLETE_INVITATION_NOT_SENT,
  );

  protected readonly formCorrectionHint = computed(() =>
    this.dspAdvertiserRequest().status === DspAdvertiserRequestStatus.PARTIALLY_COMPLETE_BILLING_UNSET
      ? "dsp-advertiser-request-status-page.billing_unset_hint"
      : "dsp-advertiser-request-status-page.invitation_not_sent_hint",
  );

  protected readonly canUpdateRequest = computed(
    () =>
      this.dspAdvertiserRequest().status === DspAdvertiserRequestStatus.PENDING ||
      this.dspAdvertiserRequest().status === DspAdvertiserRequestStatus.INCOMPLETE,
  );

  readonly statusBadgeColor = computed(() => {
    switch (this.dspAdvertiserRequest().status) {
      case DspAdvertiserRequestStatus.PENDING:
      case DspAdvertiserRequestStatus.PENDING_AUTOMATED:
      case DspAdvertiserRequestStatus.IN_PROGRESS:
      case DspAdvertiserRequestStatus.IN_PROGRESS_ADVERTISER_CREATED:
      case DspAdvertiserRequestStatus.IN_PROGRESS_BILLING_UPDATED:
      case DspAdvertiserRequestStatus.IN_PROGRESS_INVITATION_SENT:
        return "gray";
      case DspAdvertiserRequestStatus.INCOMPLETE:
      case DspAdvertiserRequestStatus.PARTIALLY_COMPLETE_BILLING_UNSET:
      case DspAdvertiserRequestStatus.PARTIALLY_COMPLETE_INVITATION_NOT_SENT:
        return "orange";
      case DspAdvertiserRequestStatus.COMPLETED:
        return "green";
      case DspAdvertiserRequestStatus.PARTIALLY_COMPLETED:
      case DspAdvertiserRequestStatus.PARTIALLY_COMPLETED_NOT_LINKED:
        return "orange";
      case DspAdvertiserRequestStatus.REJECTED:
        return "red";
    }
  });

  readonly statusBadgeLabel = computed(() => {
    switch (this.dspAdvertiserRequest().status) {
      case DspAdvertiserRequestStatus.PENDING:
        return "dsp-advertiser-request-status-page.pending_status";
      case DspAdvertiserRequestStatus.PENDING_AUTOMATED:
      case DspAdvertiserRequestStatus.IN_PROGRESS_ADVERTISER_CREATED:
      case DspAdvertiserRequestStatus.IN_PROGRESS_BILLING_UPDATED:
      case DspAdvertiserRequestStatus.IN_PROGRESS_INVITATION_SENT:
      case DspAdvertiserRequestStatus.IN_PROGRESS:
        return "dsp-advertiser-request-status-page.in_progress_status";
      case DspAdvertiserRequestStatus.INCOMPLETE:
        return "dsp-advertiser-request-status-page.incomplete_status";
      case DspAdvertiserRequestStatus.PARTIALLY_COMPLETE_BILLING_UNSET:
        return "dsp-advertiser-request-status-page.billing_unset_status";
      case DspAdvertiserRequestStatus.PARTIALLY_COMPLETE_INVITATION_NOT_SENT:
        return "dsp-advertiser-request-status-page.invitation_not_sent_status";
      case DspAdvertiserRequestStatus.COMPLETED:
        return "dsp-advertiser-request-status-page.completed_status";
      case DspAdvertiserRequestStatus.PARTIALLY_COMPLETED:
      case DspAdvertiserRequestStatus.PARTIALLY_COMPLETED_NOT_LINKED:
        return "dsp-advertiser-request-status-page.partially_completed_status";
      case DspAdvertiserRequestStatus.REJECTED:
        return "dsp-advertiser-request-status-page.rejected_status";
    }
  });

  constructor() {
    interval(AUTO_RELOAD_INTERVAL_MS)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.dspAdvertiserRequestService.reload());
  }

  protected reloadStatus() {
    if (this.isReloadingStatus()) return;
    this.isReloadingStatus.set(true);
    this.dspAdvertiserRequestService.reload();
    timer(MANUAL_RELOAD_COOLDOWN_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.isReloadingStatus.set(false));
  }
}
